import { ComponentType } from "react";
import {
  AiGradient,
  HomeGradient,
  MicGradient,
  RssGradient,
  LayersGradient,
  FileTextGradient,
  DatabaseGradient,
  Gamepad2Gradient,
  CalendarGradient,
  FolderOpenGradient,
  FilmGradient,
  BarChart3Gradient,
  EyeGradient,
  MessageSquareGradient,
  MonitorPlayGradient,
  ImageIconGradient,
  VideoGradient,
  ZapGradient,
  TrendingUpGradient,
  SparklesGradient,
  SearchGradient,
  Wand2Gradient,
  PenToolGradient,
  BrainGradient,
  ChipGradient,
} from "@/components/icons";
import { LayoutDashboard, Users, Settings, Shield, Activity, Upload, Store } from "lucide-react";
import { SidebarView } from "@/components/sidebar/types";

export interface AppDefinition {
  id: string;
  title: string;
  route: string | null; // null for submenu items
  icon: ComponentType<any>;
  category: "core" | "content" | "ai" | "admin" | "social" | "tools" | "studio" | "submenu";
  description: string;
  searchable: boolean;
  requiresAuth: boolean;
  requiresAdmin?: boolean;
  requiresPlan?: "starter" | "creator" | "growth" | "scale" | "command";
  isSubmenu?: boolean;
  submenuView?: SidebarView;
  isPinnable?: boolean;
  showInSidebar?: boolean;
  fullScreen?: boolean; // For apps like Audio Orb, AI Chat
  tags?: string[];
}

export const APP_REGISTRY: AppDefinition[] = [
  // Core Navigation
  {
    id: "home",
    title: "Home",
    route: "/dashboard",
    icon: HomeGradient,
    category: "core",
    description: "Your command center",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["dashboard", "home", "overview"],
  },
  {
    id: "feed",
    title: "Feed",
    route: "/feed",
    icon: MonitorPlayGradient,
    category: "content",
    description: "Your personalized content feed",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["feed", "content", "discovery"],
  },
  {
    id: "my-vault",
    title: "Content Vault",
    route: "/my-vault",
    icon: FolderOpenGradient,
    category: "content",
    description: "Manage your content vault",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["vault", "saved", "content"],
  },
  {
    id: "virality-lab",
    title: "Virality Lab",
    route: "/virality-lab",
    icon: ZapGradient,
    category: "tools",
    description: "Generate viral memes and ignite reach",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["memes", "viral", "ignition", "channels"],
  },
  {
    id: "content-planner",
    title: "Content Planner",
    route: "/content-planner",
    icon: CalendarGradient,
    category: "tools",
    description: "Schedule and organize posts",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["planner", "calendar", "schedule"],
  },
  {
    id: "my-ai-twin",
    title: "My AI Twin",
    route: "/my-ai-twin",
    icon: AiGradient,
    category: "ai",
    description: "Your AI content generator",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["ai", "twin", "generator"],
  },
  {
    id: "usage-dashboard",
    title: "My Usage",
    route: "/usage-dashboard",
    icon: BarChart3Gradient,
    category: "tools",
    description: "Track your monthly app usage",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false, // Moved to Settings
    tags: ["usage", "limits", "dashboard", "tracking"],
  },
  {
    id: "navigation-demo",
    title: "Navigation Demo",
    route: "/navigation-demo",
    icon: LayersGradient,
    category: "tools",
    description: "Interactive navigation component showcase",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false, // Hidden - demo feature
    tags: ["navigation", "demo", "components", "ui"],
  },
  {
    id: "create-content-submenu",
    title: "Create Content",
    route: null,
    icon: AiGradient,
    category: "submenu",
    description: "AI-powered content creation",
    searchable: false,
    requiresAuth: true,
    isSubmenu: true,
    submenuView: "create-content",
    showInSidebar: true,
    tags: [],
  },

  // Submenus
  {
    id: "dashboard-submenu",
    title: "Dashboard",
    route: null,
    icon: LayoutDashboard,
    category: "submenu",
    description: "Quick access to dashboard tools",
    searchable: false,
    requiresAuth: true,
    isSubmenu: true,
    submenuView: "dashboard",
    showInSidebar: false, // HIDDEN - Redundant
    tags: [],
  },
  {
    id: "studio-submenu",
    title: "Studio",
    route: null,
    icon: LayersGradient,
    category: "submenu",
    description: "Content creation studio",
    searchable: false,
    requiresAuth: true,
    isSubmenu: true,
    submenuView: "studio",
    showInSidebar: false, // HIDDEN - Redundant
    tags: [],
  },
  {
    id: "text-forge-submenu",
    title: "Text Forge",
    route: null,
    icon: FileTextGradient,
    category: "submenu",
    description: "Text content creation",
    searchable: false,
    requiresAuth: true,
    isSubmenu: true,
    submenuView: "meta-copy",
    showInSidebar: false, // HIDDEN - Redundant
    tags: [],
  },
  {
    id: "meta-master-submenu",
    title: "Meta Master",
    route: null,
    icon: DatabaseGradient,
    category: "submenu",
    description: "Advanced content tools",
    searchable: false,
    requiresAuth: true,
    isSubmenu: true,
    submenuView: "meta-master",
    showInSidebar: false, // HIDDEN - Redundant
    tags: [],
  },
  {
    id: "ai-agents-submenu",
    title: "AI Agents",
    route: null,
    icon: BrainGradient,
    category: "submenu",
    description: "Powerful AI automation agents",
    searchable: false,
    requiresAuth: true,
    isSubmenu: true,
    submenuView: "ai-agents",
    showInSidebar: false, // HIDDEN - Low usage
    isPinnable: true,
    tags: ["ai", "agents", "automation", "power-tools"],
  },
  {
    id: "reels-lab",
    title: "Reels",
    route: "/reels-lab",
    icon: FilmGradient,
    category: "content",
    description: "Create viral reels with AI",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false, // Shown in Create Content submenu
    tags: ["reels", "video", "viral", "seth", "creator"],
  },
  {
    id: "reels-lab-editor",
    title: "Reels Lab Editor",
    route: "/reels-lab-editor",
    icon: VideoGradient,
    category: "studio",
    description: "Pro video editor for reels",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["editor", "video", "reels", "pro"],
  },
  {
    id: "apps-submenu",
    title: "Apps",
    route: null,
    icon: LayoutDashboard,
    category: "submenu",
    description: "Browse all available apps",
    searchable: false,
    requiresAuth: true,
    isSubmenu: true,
    submenuView: "apps",
    showInSidebar: false, // HIDDEN - Use search/Explore instead
    tags: [],
  },
  {
    id: "ai-chat-submenu",
    title: "AI Chat",
    route: null,
    icon: MessageSquareGradient,
    category: "submenu",
    description: "Chat with AI assistants",
    searchable: false,
    requiresAuth: true,
    isSubmenu: true,
    submenuView: "ai-chat",
    showInSidebar: true,
    tags: [],
  },
  {
    id: "explore-submenu",
    title: "Explore",
    route: null,
    icon: RssGradient,
    category: "submenu",
    description: "Discover trending content",
    searchable: false,
    requiresAuth: true,
    isSubmenu: true,
    submenuView: "explore",
    showInSidebar: true,
    tags: [],
  },

  // Main Apps
  {
    id: "meme-vault",
    title: "Meme Vault",
    route: "/meme-vault",
    icon: FilmGradient,
    category: "content",
    description: "Your meme collection",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["meme", "vault", "collection"],
  },
  {
    id: "viral-library",
    title: "Viral Intelligence Hub",
    route: "/viral-library",
    icon: DatabaseGradient,
    category: "tools",
    description: "Access proven viral hooks, formats, and frameworks",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["viral", "library", "intelligence", "hooks", "frameworks", "formats"],
  },
  {
    id: "social",
    title: "Social",
    route: "/social",
    icon: Users,
    category: "social",
    description: "Social feed and community",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false, // HIDDEN - Low usage
    tags: ["social", "feed", "community"],
  },
  {
    id: "rowdy-rule",
    title: "90-Second Game",
    route: "/rowdy-rule",
    icon: Gamepad2Gradient,
    category: "tools",
    description: "Interactive engagement game",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false, // HIDDEN - Non-essential game
    tags: ["game", "engagement", "interactive"],
  },

  // Content Generation Apps
  {
    id: "viral-loop",
    title: "Viral Loop Engine",
    route: "/viral-loop",
    icon: LayersGradient,
    category: "ai",
    description: "Generate viral posts with engagement loops",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["viral", "loop", "engagement", "posts"],
  },
  {
    id: "viral-loop-v2",
    title: "Viral Loop V2",
    route: "/viral-loop-v2",
    icon: LayersGradient,
    category: "ai",
    description: "Enhanced viral loop generator",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["viral", "loop", "v2"],
  },
  {
    id: "viral-story-alchemist",
    title: "Viral Story Alchemist",
    route: "/viral-story-alchemist",
    icon: FileTextGradient,
    category: "ai",
    description: "Craft viral emotionally-charged stories",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["story", "viral", "content", "text"],
  },
  {
    id: "post-igniter",
    title: "Post Igniter",
    route: "/post-igniter",
    icon: ZapGradient,
    category: "ai",
    description: "Ultimate viral post generator",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["posts", "viral", "generator", "ai"],
  },
  {
    id: "post-igniter-v2",
    title: "Post Igniter V2",
    route: "/post-igniter-v2",
    icon: ZapGradient,
    category: "ai",
    description: "Enhanced post generator",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["posts", "viral", "v2"],
  },
  {
    id: "psycho-viral-creator",
    title: "Psycho Viral Creator",
    route: "/psycho-viral-creator",
    icon: BrainGradient,
    category: "ai",
    description: "Psychology-based viral content",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["psychology", "viral", "creator"],
  },
  {
    id: "nano-banana",
    title: "Nano Banana",
    route: "/nano-banana",
    icon: ImageIconGradient,
    category: "ai",
    description: "Luxury Persona Studio",
    searchable: true,
    requiresAuth: true,
    requiresPlan: "scale",
    isPinnable: true,
    showInSidebar: false,
    tags: ["image", "ai", "luxury", "persona"],
  },
  {
    id: "social-post-generator",
    title: "Social Post Generator",
    route: "/social-post-generator",
    icon: ImageIconGradient,
    category: "ai",
    description: "AI-powered social media post graphics",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["social", "post", "graphics", "ai twin"],
  },
  {
    id: "content-architect",
    title: "Content Architect",
    route: "/content-architect",
    icon: PenToolGradient,
    category: "ai",
    description: "Advanced content structuring",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["content", "architect", "structure"],
  },
  {
    id: "viral-content-studio",
    title: "Viral Content Studio",
    route: "/viral-content-studio",
    icon: LayersGradient,
    category: "studio",
    description: "Complete content creation studio",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["studio", "content", "viral"],
  },

  // Media Generation
  {
    id: "meme-generator",
    title: "Meme Generator",
    route: "/meme-generator",
    icon: FilmGradient,
    category: "ai",
    description: "Generate viral memes with AI",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["meme", "generator", "ai", "viral"],
  },
  {
    id: "metamasters-meme-studio",
    title: "MetaMasters Meme Studio",
    route: "/metamasters-meme-studio",
    icon: SparklesGradient,
    category: "ai",
    description: "All-in-one viral meme creation studio with AI ideas, generator & editor",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["meme", "metamasters", "studio", "viral", "editor", "generator"],
  },
  {
    id: "meme-remix",
    title: "Meme Remix",
    route: "/meme-remix",
    icon: FilmGradient,
    category: "ai",
    description: "Rewrite and regenerate memes",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["meme", "remix", "regenerate"],
  },
  {
    id: "twin-image-generator",
    title: "Twin Image Generator",
    route: "/twin-image-generator",
    icon: ImageIconGradient,
    category: "ai",
    description: "Generate AI twin images",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["twin", "image", "ai", "generator"],
  },
  {
    id: "ai-photo-editor",
    title: "AI Photo Editor",
    route: "/ai-photo-editor",
    icon: ImageIconGradient,
    category: "ai",
    description: "Edit photos with AI",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["photo", "editor", "ai", "image"],
  },
  {
    id: "faceless-video-generator",
    title: "Faceless Video Generator",
    route: "/faceless-video-generator",
    icon: VideoGradient,
    category: "ai",
    description: "Create faceless videos",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["video", "faceless", "generator"],
  },
  {
    id: "viral-video-generator",
    title: "Viral Video Generator",
    route: "/viral-video-generator",
    icon: VideoGradient,
    category: "ai",
    description: "Generate viral videos",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["video", "viral", "generator"],
  },
  {
    id: "luma-video-generator",
    title: "Luma Dream Machine",
    route: "/luma-video-generator",
    icon: SparklesGradient,
    category: "ai",
    description: "Generate 5-10 second AI videos from text or images",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["video", "luma", "ai", "generator", "dream-machine"],
  },
  {
    id: "veo-ai",
    title: "Veo AI",
    route: "/veo-ai",
    icon: VideoGradient,
    category: "ai",
    description: "Veo AI video generation",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["veo", "ai", "video"],
  },
  {
    id: "sora-ai",
    title: "Sora AI",
    route: "/sora-ai",
    icon: VideoGradient,
    category: "ai",
    description: "Sora AI video generation",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["sora", "ai", "video"],
  },
  {
    id: "imagen-grok",
    title: "Imagen Grok",
    route: "/imagen-grok",
    icon: ImageIconGradient,
    category: "ai",
    description: "Advanced image generation",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["imagen", "grok", "ai", "image"],
  },
  {
    id: "sprite-animator",
    title: "Sprite Animator",
    route: "/sprite-animator",
    icon: FilmGradient,
    category: "tools",
    description: "Create sprite animations",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["sprite", "animation", "creator"],
  },

  // Analysis & Tools
  {
    id: "virality-score",
    title: "Virality Score",
    route: "/virality-score",
    icon: SparklesGradient,
    category: "tools",
    description: "Analyze content virality potential",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["virality", "score", "analysis"],
  },
  {
    id: "user-hq",
    title: "User HQ Analytics",
    route: "/user-hq",
    icon: BarChart3Gradient,
    category: "tools",
    description: "Analyze social media analytics",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["analytics", "hq", "data", "insights"],
  },
  {
    id: "surveillance-hq",
    title: "Surveillance HQ",
    route: "/surveillance-hq",
    icon: EyeGradient,
    category: "tools",
    description: "Monitor content performance and track competitors",
    searchable: true,
    requiresAuth: true,
    requiresPlan: "scale",
    isPinnable: true,
    showInSidebar: false,
    tags: ["surveillance", "monitoring", "analytics", "competitors"],
  },
  {
    id: "audience-dashboard",
    title: "Audience Command Center",
    route: "/audience-dashboard",
    icon: BarChart3Gradient,
    category: "tools",
    description: "Unified dashboard with intelligence, segmentation, and optimization insights.",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["audience", "dashboard", "intelligence", "segmentation", "optimization", "overview"],
  },
  {
    id: "audience-intelligence",
    title: "Audience Intelligence Panel",
    route: "/audience-intelligence",
    icon: Users,
    category: "tools",
    description: "Deep insights into your Facebook audience composition and quality.",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["audience", "intelligence", "analytics", "facebook", "insights", "quality"],
  },
  {
    id: "audience-import",
    title: "Audience Import",
    route: "/audience-intelligence/import",
    icon: Upload,
    category: "tools",
    description: "Import your Facebook friends or followers list for intelligence analysis.",
    searchable: true,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["audience", "import", "facebook", "data"],
  },
  {
    id: "audience-segmentation",
    title: "Audience Segmentation",
    route: "/audience-segmentation",
    icon: Users,
    category: "tools",
    description: "Segment your audience into High Value Creators, Potential Buyers, and Dead Weight with export functionality.",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["audience", "segmentation", "analytics", "export", "creators", "buyers"],
  },
  {
    id: "optimization-hub",
    title: "Optimization Hub",
    route: "/optimization-hub",
    icon: TrendingUpGradient,
    category: "tools",
    description: "Get actionable recommendations to optimize your audience for maximum viral potential.",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["optimization", "recommendations", "audience", "improve"],
  },
  {
    id: "topic-generator",
    title: "Topic Generator",
    route: "/topic-generator",
    icon: SparklesGradient,
    category: "ai",
    description: "Generate content topics",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["topic", "generator", "ideas"],
  },

  // Explore & Social
  {
    id: "explore",
    title: "Explore",
    route: "/explore",
    icon: RssGradient,
    category: "content",
    description: "Discover trending content",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["explore", "discover", "trending"],
  },
  {
    id: "social-pulse",
    title: "Social Pulse",
    route: "/social-pulse",
    icon: TrendingUpGradient,
    category: "social",
    description: "Real-time social trends",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["social", "pulse", "trends"],
  },
  {
    id: "algorithmic-pulse",
    title: "Algorithmic Pulse",
    route: "/algorithmic-pulse",
    icon: Activity,
    category: "social",
    description: "Real-time content performance visualization",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["algorithmic", "pulse", "visualization", "performance", "analytics"],
  },
  {
    id: "social-feed-following",
    title: "Following Feed",
    route: "/social/following",
    icon: Users,
    category: "social",
    description: "Posts from people you follow",
    searchable: true,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["social", "following", "feed"],
  },

  // AI Chat
  {
    id: "ai-chat",
    title: "AI Chat",
    route: "/ai-chat",
    icon: MessageSquareGradient,
    category: "ai",
    description: "Chat with AI assistants",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    fullScreen: true,
    tags: ["chat", "ai", "assistant"],
  },

  // God Mode GPT (NEW - Multi-tool orchestrator)
  {
    id: "god-mode",
    title: "God Mode GPT",
    route: "/god-mode",
    icon: BrainGradient,
    category: "ai",
    description: "Multi-tool AI orchestrator for complex campaigns",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    fullScreen: true,
    tags: ["ai", "orchestrator", "automation", "god-mode", "campaign"],
  },

  // Auto-Strategist (NEW - AI-powered strategy recommendations)
  {
    id: "auto-strategist",
    title: "Auto-Strategist",
    route: "/auto-strategist",
    icon: BrainGradient,
    category: "ai",
    description: "AI analyzes your behavior and suggests personalized strategies",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["ai", "strategist", "recommendations", "automation", "analytics"],
  },

  // AI Agent Builder (NEW)
  {
    id: "ai-agent-builder",
    title: "AI Agent Builder",
    route: "/ai-agent-builder",
    icon: ChipGradient,
    category: "ai",
    description: "Create custom AI agents trained on your style",
    searchable: true,
    requiresAuth: true,
    requiresPlan: "creator",
    isPinnable: true,
    showInSidebar: false,
    tags: ["ai", "agent", "builder", "custom", "automation"],
  },

  // AI Video Generator (NEW - Video script generation with AI Twin)
  {
    id: "ai-video-generator",
    title: "AI Video Generator",
    route: "/ai-video-generator",
    icon: VideoGradient,
    category: "ai",
    description: "Generate viral video scripts with AI Twin voice",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["video", "ai", "scripts", "faceless", "generator", "viral", "twin"],
  },

  // User Pages
  {
    id: "profile",
    title: "Profile",
    route: "/profile",
    icon: Users,
    category: "core",
    description: "Your account settings",
    searchable: true,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["profile", "account", "settings"],
  },
  {
    id: "settings",
    title: "Settings",
    route: "/settings",
    icon: Settings,
    category: "core",
    description: "App settings and preferences",
    searchable: true,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["settings", "preferences", "configuration"],
  },
  {
    id: "achievements",
    title: "Achievements",
    route: "/achievements",
    icon: SparklesGradient,
    category: "core",
    description: "Your achievements and rewards",
    searchable: true,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["achievements", "rewards", "badges"],
  },
  {
    id: "notifications",
    title: "Notifications",
    route: "/notifications",
    icon: MessageSquareGradient,
    category: "core",
    description: "Your notifications",
    searchable: true,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["notifications", "alerts"],
  },
  {
    id: "support",
    title: "Support",
    route: "/support",
    icon: MessageSquareGradient,
    category: "core",
    description: "Get help and support",
    searchable: true,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["support", "help", "tickets"],
  },
  {
    id: "compliance-dashboard",
    title: "Compliance & Recovery",
    route: "/compliance-dashboard",
    icon: Shield,
    category: "core",
    description: "Report and track compliance issues",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["compliance", "recovery", "support", "issues", "lq1"],
  },
  {
    id: "compliance-admin",
    title: "Compliance Admin",
    route: "/compliance-admin",
    icon: Shield,
    category: "admin",
    description: "Manage all compliance tickets",
    searchable: true,
    requiresAuth: true,
    requiresAdmin: true,
    isPinnable: false,
    showInSidebar: false, // Moved to top bar
    tags: ["compliance", "admin", "tickets", "management"],
  },
  {
    id: "nano-banana-simple",
    title: "Nano Banana Creator",
    route: "/nano-banana-creator",
    icon: ImageIconGradient,
    category: "content",
    description: "Quick AI image generation",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["ai", "image", "generator", "nano"],
  },
  {
    id: "content-studio",
    title: "Content Studio",
    route: "/content-studio",
    icon: SparklesGradient,
    category: "content",
    description: "Your content command center",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: false,
    tags: ["content", "studio", "hub", "creation"],
  },
  {
    id: "help",
    title: "Help Center",
    route: "/help",
    icon: MessageSquareGradient,
    category: "core",
    description: "Get help and learn how to use Command OS",
    searchable: true,
    requiresAuth: false,
    isPinnable: false,
    showInSidebar: false,
    tags: ["help", "support", "faq", "guide"],
  },

  // Admin
  {
    id: "admin-panel",
    title: "Admin Panel",
    route: "/admin-panel",
    icon: Shield,
    category: "admin",
    description: "Administration dashboard",
    searchable: false,
    requiresAuth: true,
    requiresAdmin: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["admin", "dashboard", "management"],
  },
  {
    id: "plan-settings",
    title: "Plan Settings",
    route: "/admin-panel/plans",
    icon: Shield,
    category: "admin",
    description: "Manage subscription plans",
    searchable: false,
    requiresAuth: true,
    requiresAdmin: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["admin", "plans", "subscriptions"],
  },
  {
    id: "mac-test",
    title: "Mac OS Test",
    route: "/mac-test",
    icon: Settings,
    category: "admin",
    description: "MacOS menu bar component test",
    searchable: true,
    requiresAuth: true,
    requiresAdmin: false,
    isPinnable: true,
    showInSidebar: false,
    tags: ["test", "mac", "menu", "ui"],
  },

  // AI Twin Pages
  {
    id: "my-ai-twin-training",
    title: "AI Twin Training",
    route: "/my-ai-twin/train",
    icon: AiGradient,
    category: "ai",
    description: "Train your AI twin",
    searchable: false,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: [],
  },
  {
    id: "studio-apps",
    title: "Studio",
    route: "/studio",
    icon: VideoGradient,
    category: "studio",
    description: "Your complete media creation hub",
    searchable: false,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: [],
  },
  {
    id: "textforge-apps",
    title: "Text Forge",
    route: "/textforge",
    icon: FileTextGradient,
    category: "content",
    description: "Craft compelling content that converts",
    searchable: false,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: [],
  },
  {
    id: "metamaster-apps",
    title: "Meta Master",
    route: "/metamaster",
    icon: TrendingUpGradient,
    category: "tools",
    description: "Master your content strategy",
    searchable: false,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: [],
  },
  {
    id: "autonomous-learning",
    title: "Autonomous Learning",
    route: "/autonomous-learning",
    icon: BrainGradient,
    category: "ai",
    description: "AI learns from your behavior and optimizes content strategy automatically",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["ai", "learning", "autonomous", "intelligence", "optimization", "analytics"],
  },
  {
    id: "marketplace",
    title: "Marketplace",
    route: "/marketplace",
    icon: Store,
    category: "core",
    description: "Discover apps, integrations, and templates to extend Command AIOS",
    searchable: true,
    requiresAuth: true,
    isPinnable: true,
    showInSidebar: true,
    tags: ["marketplace", "apps", "integrations", "templates", "store"],
  },
  {
    id: "apps",
    title: "Apps",
    route: "/apps",
    icon: LayoutDashboard,
    category: "core",
    description: "Browse all available apps",
    searchable: true,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["apps", "browse", "tools"],
  },
  {
    id: "create-content",
    title: "Create Content",
    route: "/create-content",
    icon: AiGradient,
    category: "content",
    description: "Content creation studio",
    searchable: true,
    requiresAuth: true,
    isPinnable: false,
    showInSidebar: false,
    tags: ["create", "content", "studio"],
  },
];

// Utility functions
export const getSearchableApps = (): AppDefinition[] =>
  APP_REGISTRY.filter((app) => app.searchable && app.route !== null);

export const getPinnableApps = (): AppDefinition[] => APP_REGISTRY.filter((app) => app.isPinnable);

export const getSidebarApps = (): AppDefinition[] => APP_REGISTRY.filter((app) => app.showInSidebar);

export const getCoreApps = (): AppDefinition[] => APP_REGISTRY.filter((app) => app.category === "core");

export const getAdminApps = (): AppDefinition[] => APP_REGISTRY.filter((app) => app.requiresAdmin);

export const getAppById = (id: string): AppDefinition | undefined => APP_REGISTRY.find((app) => app.id === id);

export const getAppByRoute = (route: string): AppDefinition | undefined =>
  APP_REGISTRY.find((app) => app.route === route);

export const getAppsByCategory = (category: string): AppDefinition[] =>
  APP_REGISTRY.filter((app) => app.category === category);
